import type { Trip, TripSummary, v3 } from '@greensm/api-client';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useServices } from '@/app/services';
import { qk } from '@/shared/lib/queryKeys';

export type HistoryFilter = 'ALL' | 'COMPLETED' | 'CANCELLED';

/** Phân trang theo cursor của Backend. Gộp trang và KHỬ TRÙNG theo id ⇒ không lặp / không bỏ bản ghi. */
export function useTripHistory(filter: HistoryFilter) {
  const { api, v3: apiV3, v3Enabled } = useServices();
  const q = useInfiniteQuery({
    queryKey: v3Enabled ? qk.v3.history(filter) : qk.history(filter),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const params = { cursor: pageParam, state: filter === 'ALL' ? undefined : filter, limit: 8 };
      if (!v3Enabled) return api.trips.list(params, signal);
      const page = await apiV3.customer.history(params, signal);
      return { items: page.trips.map((view): TripSummary => ({
        id: view.trip_id, code: view.trip_id, state: tripState(view.stage),
        productName: view.vehicle_class === 'CAR_7' ? 'Green Car - 7 khách' : 'Green Car - 4 khách',
        startedAt: view.created_at, originLabel: view.pickup.label,
        destinationLabel: view.dropoff.label, priceVnd: view.price_vnd,
      })), nextCursor: page.next_cursor };
    },
    refetchOnMount: 'always',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = useMemo(() => dedupeById(q.data?.pages.flatMap((p) => p.items) ?? []), [q.data]);
  return { ...q, items };
}

export function dedupeById<T extends { id: string }>(list: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const x of list) if (!seen.has(x.id)) { seen.add(x.id); out.push(x); }
  return out;
}

export function useTripDetail(id: string) {
  const { api, v3: apiV3, v3Enabled } = useServices();
  return useQuery({ queryKey: v3Enabled ? qk.v3.historyTrip(id) : qk.trip(id), queryFn: async ({ signal }) => {
    if (!v3Enabled) return api.trips.detail(id, signal);
    return historyTrip(await apiV3.trips.get(id, signal));
  } });
}

export function useTripTimeline(id: string) {
  const { api, v3: apiV3, v3Enabled } = useServices();
  return useQuery({ queryKey: v3Enabled ? qk.v3.historyTimeline(id) : qk.tripTimeline(id), queryFn: async ({ signal }) => {
    if (!v3Enabled) return api.trips.timeline(id, signal);
    const trip = await apiV3.trips.get(id, signal);
    return [{ at: trip.created_at, label: 'Tạo chuyến' },
      { at: trip.updated_at, label: trip.stage === 'COMPLETED' ? 'Hoàn thành' : trip.stage === 'CANCELLED' ? 'Đã hủy' : 'Cập nhật chuyến' }];
  } });
}

function tripState(stage: v3.TripView['stage']): Trip['state'] {
  return stage === 'SEARCHING' || stage === 'OFFERED' ? 'MATCHING' : stage === 'DRIVER_EN_ROUTE'
    ? 'ARRIVING' : stage === 'ON_TRIP' ? 'IN_PROGRESS' : stage;
}

function historyTrip(view: v3.TripView): Trip {
  return { id: view.trip_id, code: view.trip_id, state: tripState(view.stage),
    productName: view.vehicle_class === 'CAR_7' ? 'Green Car - 7 khách' : 'Green Car - 4 khách',
    createdAt: view.created_at, priceVnd: view.price_vnd, paymentMethod: 'CASH_QR', allowedActions: [],
    origin: { address: view.pickup.label, detail: view.pickup.address ?? undefined,
      point: { lat: view.pickup.point.lat, lng: view.pickup.point.lon } },
    destination: { address: view.dropoff.label, detail: view.dropoff.address ?? undefined,
      point: { lat: view.dropoff.point.lat, lng: view.dropoff.point.lon } },
    distanceKm: view.route ? view.route.distance_m / 1000 : undefined,
    durationMinutes: view.route ? Math.ceil(view.route.duration_s / 60) : undefined,
    driver: view.driver ? { name: view.driver.display_name, vehicle: view.driver.vehicle_label,
      plate: view.driver.plate_masked } : undefined };
}

export const isFinished = (s: TripSummary['state']) => s === 'COMPLETED' || s === 'CANCELLED';
